import { useCallback, useEffect, useRef, useState } from 'react';
import { buildSportsbookBetslipDeeplink } from '@low6-pickem/sports-config';
import {
  buildBetslipPromptSeenKey,
  hasSeenBetslipPrompt,
  markBetslipPromptSeen,
  shouldOpenBetslipPrompt,
} from '@/utils/sportsbookBetslipPrompt';

type UseSportsbookBetslipPromptArgs = {
  /** Live prop contest, player has a pick, not history, not past cutoff. */
  enabled: boolean;
  dayId: string | null | undefined;
  /** Contest league code from `pickems.sport`. */
  sport: string | null | undefined;
  selectionIds: readonly (string | null | undefined)[];
};

type UseSportsbookBetslipPromptResult = {
  isOpen: boolean;
  /** Non-null whenever a handoff is actually possible; drives both prompt and CTA. */
  deeplink: string | null;
  dismiss: () => void;
};

/**
 * Opens the Sportsbook betslip prompt once per reveal day.
 *
 * "Seen" is recorded when the prompt opens, not when it is dismissed. A player who
 * backgrounds the app or hard-refreshes while it is up has still been prompted, and
 * marking on dismiss would re-show it on every reload until they explicitly closed it.
 */
export function useSportsbookBetslipPrompt({
  enabled,
  dayId,
  sport,
  selectionIds,
}: UseSportsbookBetslipPromptArgs): UseSportsbookBetslipPromptResult {
  const deeplink = buildSportsbookBetslipDeeplink(sport, selectionIds);
  const [isOpen, setIsOpen] = useState(false);
  // Survives the re-render caused by opening, so the effect cannot re-fire for the
  // same day even before the storage write lands.
  const openedForDayRef = useRef<string | null>(null);

  useEffect(() => {
    if (!dayId || openedForDayRef.current === dayId) return;

    const key = buildBetslipPromptSeenKey(dayId);
    const shouldOpen = shouldOpenBetslipPrompt({
      enabled,
      dayId,
      deeplink,
      alreadySeen: hasSeenBetslipPrompt(key),
    });
    if (!shouldOpen) return;

    openedForDayRef.current = dayId;
    markBetslipPromptSeen(key);
    setIsOpen(true);
  }, [enabled, dayId, deeplink]);

  const dismiss = useCallback(() => setIsOpen(false), []);

  return { isOpen, deeplink, dismiss };
}
